import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';
import { useTranslation } from 'react-i18next';

export default function FlexiblePickupPage() {
  const { t } = useTranslation();
  const timeSlots = [
    {
      id: 'morning',
      label: 'Ranní vyzvednutí',
      time: '07:00 - 08:30',
      icon: 'ri-sun-line',
      description: 'Ideální pro ty, kteří odjíždějí brzy do práce. Vyzvedneme prádlo ještě před vaším odchodem.',
      color: 'from-amber-400 to-orange-500'
    },
    {
      id: 'noon',
      label: 'Dopoledne',
      time: '08:30 - 12:00',
      icon: 'ri-sun-fill',
      description: 'Vhodné pro práci z domova nebo volné dopoledne.',
      color: 'from-yellow-400 to-amber-500'
    },
    {
      id: 'afternoon',
      label: 'Odpoledne',
      time: '12:00 - 16:00',
      icon: 'ri-cloudy-line',
      description: 'Pro ty, kteří mají flexibilní pracovní dobu.',
      color: 'from-sky-400 to-blue-500'
    },
    {
      id: 'evening',
      label: 'Večerní vyzvednutí',
      time: '18:00 - 20:00',
      icon: 'ri-moon-line',
      description: 'Perfektní pro ty, kteří se vracejí z práce později. Vyzvedneme, až budete doma.',
      color: 'from-indigo-400 to-purple-500'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Přizpůsobíme se vám',
      description: 'Víme, že váš čas je cenný. Proto nabízíme široké časové rozmezí pro vyzvednutí.'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Vyzvednutí v práci',
      description: 'Nemůžete být doma? Rádi vyzvedneme prádlo přímo u vás v kanceláři.'
    },
    {
      icon: 'ri-shield-check-line',
      title: '100% diskrétní',
      description: 'Vše probíhá naprosto diskrétně. Vaše soukromí je pro nás prioritou.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Vždy vás kontaktujeme',
      description: 'Před příjezdem vám zavoláme nebo napíšeme, abyste věděli, kdy přesně dorazíme.'
    }
  ];

  return (
    <>
      <SEOHead
        title={t('seo.flexPickup.title')}
        description={t('seo.flexPickup.description')}
        keywords={t('seo.flexPickup.keywords')}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-500 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-heart-line text-lg"></i>
                Služba pro vás
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Chápeme, že jste <span className="bg-white/20 backdrop-blur-sm px-4 py-1 rounded-xl">zaneprázdnění</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8 max-w-3xl mx-auto">
                Víme, jak náročný je váš pracovní den. Proto jsme vytvořili službu, která se přizpůsobí vašemu rozvrhu - ne naopak.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="/kosik"
                  className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shopping-bag-line"></i>
                  Objednat službu
                </a>
                <a
                  href="tel:+420776166733"
                  className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/30 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  Zavolat nám
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Main Message */}
        <section className="py-20 bg-gradient-to-b from-teal-50 to-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <i className="ri-heart-3-line text-4xl text-teal-600"></i>
            </div>
            
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Tato služba je pro vás
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Rozumíme tomu, že většina našich klientů pracuje od rána do večera. Nemáte čas čekat doma na kurýra uprostřed dne. Proto nabízíme <strong className="text-gray-900">ranní vyzvednutí před odchodem do práce</strong> a <strong className="text-gray-900">večerní vyzvednutí po návratu domů</strong>.
            </p>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-teal-100">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <i className="ri-sun-line text-2xl text-amber-600"></i>
                </div>
                <span className="text-2xl font-bold text-gray-400">nebo</span>
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <i className="ri-moon-line text-2xl text-indigo-600"></i>
                </div>
              </div>
              
              <p className="text-lg text-gray-700">
                <strong className="text-teal-600">Ráno 7:00 - 8:30</strong> ještě před vaším odchodem do práce
                <br />
                <strong className="text-teal-600">Večer 18:00 - 20:00</strong> až se vrátíte domů
              </p>
            </div>
          </div>
        </section>

        {/* Time Slots */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Vyberte si čas, který vám vyhovuje
              </h2>
              <p className="text-xl text-gray-600">
                Nabízíme 4 časové intervaly pro maximální flexibilitu
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {timeSlots.map((slot) => (
                <div
                  key={slot.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border border-gray-100 group"
                >
                  <div className={`h-3 bg-gradient-to-r ${slot.color}`}></div>
                  <div className="p-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${slot.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <i className={`${slot.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {slot.label}
                    </h3>
                    <p className="text-2xl font-bold text-teal-600 mb-3">
                      {slot.time}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {slot.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Work Pickup Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full mb-6">
                  <i className="ri-building-line text-pink-600"></i>
                  <span className="text-pink-700 font-medium">Nová možnost</span>
                </div>
                
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Vyzvednutí přímo v práci
                </h2>
                
                <p className="text-xl text-gray-600 leading-relaxed mb-6">
                  Nemůžete být doma ani ráno, ani večer? Žádný problém! Rádi vyzvedneme vaše prádlo <strong className="text-gray-900">přímo u vás v kanceláři</strong> nebo na jiném místě, které vám vyhovuje.
                </p>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-6">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-edit-line text-teal-600"></i>
                    Jak na to?
                  </h4>
                  <ol className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold text-sm flex-shrink-0">1</span>
                      <span>Při objednávce napište do poznámky adresu pracoviště</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold text-sm flex-shrink-0">2</span>
                      <span>Uveďte, kde vás najdeme (recepce, kancelář, atd.)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold text-sm flex-shrink-0">3</span>
                      <span>Před příjezdem vám zavoláme</span>
                    </li>
                  </ol>
                </div>
                
                <div className="flex items-center gap-3 text-gray-600">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                  <span className="font-medium">Vše probíhá naprosto diskrétně</span>
                </div>
              </div>
              
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/e00235b93f5fd0c7c1d69bf05d39c9ea.jpg"
                  alt="Vyzvednutí v práci"
                  className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <i className="ri-lock-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">100% diskrétní</p>
                      <p className="text-sm text-gray-600">Vaše soukromí je priorita</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Proč si klienti vybírají naši službu
              </h2>
              <p className="text-xl text-gray-600">
                Jsme tu pro vás a vaše pohodlí
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="text-center p-8 bg-gradient-to-b from-gray-50 to-white rounded-2xl hover:shadow-lg transition-all border border-gray-100"
                >
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${benefit.icon} text-3xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
